import React, { useState } from 'react';
import { X, Trash2, CalendarX } from 'lucide-react';
import { Client, Evento } from "../../types/index";
import { useUpdateEventoMutation, useDeleteEventoMutation, useCancelarClaseCompletaMutation, useReestablecerClaseCompletaMutation } from '../../services/hooks';
import { useAllClientsQuery } from '../../services/hooks/useClientes';
import {
    useProspectsQuery,
    useAddProspectToEventMutation,
    useRemoveProspectFromEventMutation,
    useCreateProspectMutation,
} from '../../services/hooks/useProspects';
import { Prospect } from '../../types';
import { useEventWaitlistQuery } from '../../services/hooks/useWaitlist';
import {
    useCancelarPorClienteMutation,
    useAnadirConRecuperacionMutation,
    useClientesConRecuperacionQuery,
} from '../../services/hooks/useAsistencia';
import Select from 'react-select';
import ConfirmationModal from '../common/ConfirmationModal';
import CancelClassModal, { CancelClassOptions } from './CancelClassModal';
import InfoTooltip from '../common/InfoTooltip';
import { formatDate, idsDeInscritos } from '../../utils/utils';
import ReplicarEvento from './ReplicarEvento';
import ClassRegister from './ClassRegister';
import { IconButton, IconButtonSlot } from '../common/IconButton';
import PeopleTableHeader from './PeopleTableHeader';

// Correo y teléfono. El `min-w-0` va en el contenedor Y en el hijo: sin los dos,
// un correo largo no se trunca, se desborda y empuja la columna de al lado — el
// mismo fallo que ya salió en el listado de SEPA.
const ContactCell = ({ email, telefono }: { email?: string | null; telefono?: string | null }) => (
    <div className='col-span-12 lg:col-span-5 min-w-0 text-xs text-gray-600'>
        {email && <p className='truncate min-w-0'>{email}</p>}
        {telefono && <p className='truncate min-w-0'>{telefono}</p>}
        {!email && !telefono && <p className='text-gray-400'>Sin contacto</p>}
    </div>
);

interface EventDetailProps {
    event: any;
    onClose: () => void;
    onEditEvent: () => void;
    startDate: string;
    endDate: string;
}

const EventDetail: React.FC<EventDetailProps> = ({ event, onClose, onEditEvent, startDate, endDate }) => {
    const [selectedClientId, setSelectedClientId] = useState<string>('');
    const [showAddClient, setShowAddClient] = useState<boolean>(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [confirmationMessage, setConfirmationMessage] = useState<string>('');
    const [operation, setOperation] = useState<string>('');
    const [showReplicarEvento, setShowReplicarEvento] = useState<boolean>(false);
    const [showCancelClassModal, setShowCancelClassModal] = useState<boolean>(false);
    
    // Acceder al cache de React Query
    
    // Cargar todos los clientes para el selector
    const { data: allClients = [], isLoading: isLoadingClientes, isError: isErrorClientes } = useAllClientsQuery();
    const clientesDisponibles = allClients;

    //llamando a la mutación para modificar un evento
    const {mutateAsync: updateEvento } = useUpdateEventoMutation(startDate, endDate);

    //llamando a la mutación para eliminar un evento
    const {mutateAsync: deleteEvento } = useDeleteEventoMutation(startDate, endDate);

    // Cancelar/reestablecer la clase completa compensando a los inscritos
    const { mutateAsync: cancelarClase, isPending: isCancellingClass } = useCancelarClaseCompletaMutation(startDate, endDate);
    const { mutateAsync: reestablecerClase } = useReestablecerClaseCompletaMutation(startDate, endDate);


    // Función para manejar la eliminación de un cliente del evento
    const handleRemoveClient = (clientId: string) => {
        
        // console.log(`Eliminar cliente con ID: ${clientId}`);

        const updatedEvent: Evento = {
            _id: event._id,
            nivel_id: event.nivel_id,
            instructor_id: event.instructor_id,
            sala: event.sala,
            fecha: event.fecha,
            horaInicio: event.horaInicio,
            horaFin: event.horaFin,
            clientesInscritos: idsDeInscritos(event.clientesInscritos).filter((id) => id !== clientId),
            maxClientes: event.maxClientes,
            cancelado: event.cancelado,
        };
        updateEvento(updatedEvent);
    };


    //Función para mostrar seccion de agregar cliente
    const handleShowAddClient = () => {
        setShowAddClient(true);
    };

    // Funcion para cerrar la seccion de agregar cliente
    const handleCloseShowAddClient = () => {
        setShowAddClient(false);
    }


    // Función para manejar la adición de un cliente al evento
    const handleAddClient = () => {
        if (selectedClientId) {
            const updatedEvent: Evento = {
                _id: event._id,
                nivel_id: event.nivel_id,
                instructor_id: event.instructor_id,
                sala: event.sala,
                fecha: event.fecha,
                horaInicio: event.horaInicio,
                horaFin: event.horaFin,
                clientesInscritos: [...event.clientesInscritos.map((cliente: Client) => cliente._id), selectedClientId],
                maxClientes: event.maxClientes,
                cancelado: event.cancelado,
            };
            updateEvento(updatedEvent);
            setSelectedClientId('');
            // console.log('evento', updatedEvent);
        }
    };

    // Cancelar la clase completa. Por defecto compensa a los inscritos con una
    // recuperación y les avisa; el modal deja desactivar cada cosa por separado.
    const handleConfirmCancelClass = async ({ reason, compensate, notify }: CancelClassOptions) => {
        await cancelarClase({ eventoId: event._id, reason, compensate, notify });
        setShowCancelClassModal(false);
        onClose();
    };

    const handleRestablecerEvent = async () => {
        await reestablecerClase(event._id);
        setShowConfirmationModal(false);
    };

    const handleEliminarEvent = () => {
        deleteEvento(event._id);
        setShowConfirmationModal(false);
    };

    // D2 — personas de prueba en esta clase. Ocupan aforo igual que un cliente,
    // así que el contador y el botón de agregar cuentan las dos listas.
    const prospectosEnClase: Prospect[] = event.enlistedProspects || [];
    const ocupadas = event.clientesInscritos.length + prospectosEnClase.length;
    const claseLlena = ocupadas >= event.maxClientes;

    // D1 — cola de espera de esta clase. Solo se consulta si está llena: en una
    // clase con sitio no hay nadie esperando, y así no se pide por cada evento
    // que se abra.
    const { data: enEspera = [] } = useEventWaitlistQuery(event._id, claseLlena);

    const { data: todosLosProspectos = [] } = useProspectsQuery();
    const { mutateAsync: apuntarProspecto, isPending: apuntandoProspecto } = useAddProspectToEventMutation();
    const { mutateAsync: quitarProspecto } = useRemoveProspectFromEventMutation();
    const { mutateAsync: crearProspecto, isPending: creandoProspecto } = useCreateProspectMutation();

    const [showAddProspect, setShowAddProspect] = useState<boolean>(false);
    const [selectedProspectId, setSelectedProspectId] = useState<string>('');
    // Alta en el momento: quien está en recepción no debería tener que irse a otra
    // pantalla a crear la ficha mientras la persona espera en la puerta.
    const [nuevoProspecto, setNuevoProspecto] = useState({ name: '', phone: '', email: '' });
    const [creandoNuevo, setCreandoNuevo] = useState<boolean>(false);

    const cerrarAddProspect = () => {
        setShowAddProspect(false);
        setSelectedProspectId('');
        setCreandoNuevo(false);
        setNuevoProspecto({ name: '', phone: '', email: '' });
    };

    const handleAddProspect = async () => {
        if (!selectedProspectId) return;
        await apuntarProspecto({ eventId: event._id, prospectId: selectedProspectId });
        cerrarAddProspect();
    };

    const handleCrearYApuntar = async () => {
        const { name, phone, email } = nuevoProspecto;
        if (!name.trim() || !phone.trim() || !email.trim()) return;
        // Si el alta funciona pero apuntarla falla (clase llena, plazas reservadas
        // para la lista de espera…), la ficha se queda creada a propósito: el dato
        // de contacto es lo que no se quiere perder.
        const creado = await crearProspecto({ name, phone, email });
        await apuntarProspecto({ eventId: event._id, prospectId: creado._id });
        cerrarAddProspect();
    };

    // El centro actúa en nombre del cliente (sin correo o que llama): mismas
    // reglas que el portal. "Canceló" le da recuperación si avisa en plazo; la
    // papelera le quita sin más (para errores al apuntar).
    const [clienteACancelar, setClienteACancelar] = useState<Client | null>(null);
    const { mutateAsync: cancelarPorCliente, isPending: cancelandoPorCliente } = useCancelarPorClienteMutation();
    const handleCancelarPorCliente = async () => {
        if (!clienteACancelar) return;
        try {
            await cancelarPorCliente({ eventId: event._id, clientId: clienteACancelar._id });
        } finally {
            setClienteACancelar(null);
        }
    };

    const [showAddConRecuperacion, setShowAddConRecuperacion] = useState<boolean>(false);
    const [clienteConRecuperacionId, setClienteConRecuperacionId] = useState<string>('');
    const {
        data: clientesConRecuperacion = [],
        isLoading: cargandoConRecuperacion,
    } = useClientesConRecuperacionQuery(event._id, showAddConRecuperacion);
    const { mutateAsync: anadirConRecuperacion, isPending: anadiendoConRecuperacion } = useAnadirConRecuperacionMutation();
    const cerrarAddConRecuperacion = () => {
        setShowAddConRecuperacion(false);
        setClienteConRecuperacionId('');
    };
    const handleAnadirConRecuperacion = async () => {
        if (!clienteConRecuperacionId) return;
        await anadirConRecuperacion({ eventId: event._id, clientId: clienteConRecuperacionId });
        cerrarAddConRecuperacion();
    };

    const handleOnConfirm = () => {
        switch (operation) {
            case 'restablecer':
                handleRestablecerEvent();
                break;
            case 'eliminar':
                handleEliminarEvent();
                break;
            default:
                break
        }
    };


    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 ">
            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-xl max-h-full overflow-y-auto">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <h2 className="text-2xl font-bold mb-2 p-2 bg-slate-200 rounded-lg">{event.nivel?.nombre ?? 'Nivel eliminado'} ({event.instructor?.nombre ?? 'instructor eliminado'})</h2>
                {event.cancelado && (
                    <div className='bg-red-500 text-white p-2 text-center font-bold'>
                        CLASE CANCELADA
                        {event.cancellationReason && (
                            <span className='block font-normal text-sm'>Motivo: {event.cancellationReason}</span>
                        )}
                    </div>
                )}
                <div className='flex space-x-4 p-2'>
                    <div className='flex-1 pl-4'>
                        <div className='mb-4'>
                            <h3 className='font-bold text-lg text-green-600 mb-2'>Horario</h3>
                            <p className='p-1 text-sm'><strong>Fecha:</strong> {new Date(event.fecha).toLocaleDateString()}</p>
                            <p className='p-1 text-sm'><strong>Hora de Inicio:</strong> {event.horaInicio}</p>
                            <p className='p-1 text-sm'><strong>Hora de Fin:</strong> {event.horaFin}</p>
                        </div>
                        <div className='mb-4'>
                            <h3 className='font-bold text-lg text-green-600 mb-2'>Datos del Evento</h3>
                            <p className='p-1 text-sm'><strong>Sala:</strong> {event.sala}</p>
                            <p className='p-1 text-sm'><strong>Nivel:</strong> {event.nivel?.nombre ?? 'Nivel eliminado'}</p>
                            <p className='p-1 text-sm'><strong>Instructor:</strong> {event.instructor?.nombre ?? 'instructor eliminado'}</p>
                        </div>
                        <div className='mb-4'>
                            <h3 className='font-bold text-lg text-green-600 mb-2'>Clientes</h3>
                            <p className='p-1 text-sm'><strong>Plazas ocupadas:</strong> {ocupadas} / {event.maxClientes}</p>
                            {prospectosEnClase.length > 0 && (
                                <p className='p-1 text-sm text-purple-700'>
                                    <strong>De prueba:</strong> {prospectosEnClase.length}
                                </p>
                            )}
                            {/* <p className='p-1 text-sm'><strong>Cancelado:</strong> {event.cancelado ? 'Sí' : 'No'}</p> */}
                        </div>
                    </div>
                    <div className='flex flex-col justify-center gap-4 pr-4'>
                        <button
                            className={`text-white p-2 rounded ${claseLlena || event.cancelado ? 'bg-gray-400' : 'bg-blue-600'}`}
                            onClick={handleShowAddClient}
                            disabled={claseLlena || event.cancelado}
                        >
                                Agregar Clientes
                        </button>
                        <button
                            className={`text-white p-2 rounded ${claseLlena || event.cancelado ? 'bg-gray-400' : 'bg-purple-600'}`}
                            onClick={() => setShowAddProspect(true)}
                            disabled={claseLlena || event.cancelado}
                        >
                            Agregar Prueba
                        </button>
                        <button
                            className={`text-white p-2 rounded ${claseLlena || event.cancelado ? 'bg-gray-400' : 'bg-emerald-600'}`}
                            onClick={() => setShowAddConRecuperacion(true)}
                            disabled={claseLlena || event.cancelado}
                            title='Apuntar a un cliente gastando una de sus recuperaciones'
                        >
                            Añadir con recuperación
                        </button>
                        <button 
                            className={`text-white p-2 rounded ${event.cancelado ? 'bg-gray-400' : 'bg-blue-600'}`}
                            onClick={onEditEvent}
                            disabled={event.cancelado}
                        >
                            Editar Evento
                        </button>
                        <button 
                            className={`text-white p-2 rounded ${event.cancelado ? 'bg-gray-400' : 'bg-purple-600'}`}
                            onClick={() => setShowReplicarEvento(true)}
                            disabled={event.cancelado}
                        >
                            Replicar Evento
                        </button>
                        <button
                            className={`text-white p-2 rounded ${event.cancelado ? 'bg-gray-400' : 'bg-blue-600'}`}
                            onClick={() => setShowCancelClassModal(true)}
                            disabled={event.cancelado}
                        >
                            Cancelar Evento
                        </button>
                        {event.cancelado && (
                            <button 
                                className='bg-green-500 text-white p-2 rounded'
                                onClick={() =>{
                                    setShowConfirmationModal(true);
                                    setConfirmationMessage('¿Estás seguro de que deseas <strong>RESTABLECER</strong> este evento?');
                                    setOperation('restablecer');
                                }}
                            >
                                Reestablecer Evento
                            </button>
                        )}
                        <button 
                            className='bg-red-500 text-white p-2 rounded'
                            onClick={() => {
                                setShowConfirmationModal(true)
                                setConfirmationMessage('¿Estás seguro de que deseas <strong>ELIMINAR</strong> este evento?');
                                setOperation('eliminar');
                            }}
                        >
                            Eliminar Evento
                        </button>
                        
                    </div>
                </div>
                {/* Las tres listas de personas de esta clase comparten rejilla:
                    son lo mismo —quién está en la sala— y antes cada una era un
                    <ul> distinto, así que leerlas obligaba a recolocar la vista
                    tres veces. Lo que las separa sí se conserva: los de prueba
                    van en morado porque no tienen plaza, y la cola va numerada,
                    sin acciones, porque todavía no ocupa sitio. */}
                <div className='bg-slate-100 p-6 space-y-6'>
                    <section>
                        <h3 className='font-bold text-sm uppercase tracking-wide text-gray-600 mb-2'>
                            Participantes ({event.clientesInscritos.length})
                        </h3>
                        {/* Las tarjetas van sin `overflow-hidden`: recortaba el globo
                            del aviso de "Plaza en espera". Las esquinas de arriba las
                            redondea la cabecera por su cuenta. */}
                        {event.clientesInscritos.length > 0 ? (
                            <div className='bg-white rounded-lg border border-gray-200'>
                                <PeopleTableHeader middle='Contacto' />
                                <ul className='divide-y divide-gray-100'>
                                    {event.clientesInscritos.map((cliente: Client) => (
                                        <li key={cliente._id} className='grid grid-cols-12 gap-2 items-center px-3 py-2'>
                                            <div className='col-span-12 lg:col-span-5 min-w-0'>
                                                <p className='text-sm font-medium text-gray-900 truncate'>{cliente.nombre}</p>
                                                {cliente.enEspera && cliente.fechaInicioEspera && cliente.fechaFinEspera && (
                                                    <span className='mt-0.5 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700'>
                                                        Plaza en espera
                                                        <InfoTooltip
                                                            text={`Desde: ${formatDate(cliente.fechaInicioEspera)}
                                                                Hasta: ${formatDate(cliente.fechaFinEspera)}
                                                                Motivo: ${cliente.motivoEspera || 'No especificado'}`}
                                                            iconSize={12}
                                                            position='bottom'
                                                            align='start'
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                            <ContactCell email={cliente.email} telefono={cliente.telefono} />
                                            <div className='col-span-12 lg:col-span-2 flex gap-1 lg:justify-end'>
                                                {!event.cancelado && (
                                                    <IconButton
                                                        size='sm'
                                                        tone='neutral'
                                                        label='Ha avisado de que no viene: cancelar por el cliente (recuperación si está en plazo)'
                                                        onClick={() => setClienteACancelar(cliente)}
                                                    >
                                                        <CalendarX size={16} />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size='sm'
                                                    tone='danger'
                                                    label='Sacar de esta clase (sin recuperación)'
                                                    onClick={() => handleRemoveClient(cliente._id)}
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <p className='text-sm text-gray-500'>No hay clientes inscritos.</p>
                        )}
                    </section>

                    {clienteACancelar && (
                        <div className='mt-3 p-3 rounded border border-amber-300 bg-amber-50 text-sm'>
                            <p>
                                <strong>{clienteACancelar.nombre}</strong> no viene a esta clase. Se le saca de la
                                clase y gana una recuperación si avisa dentro del plazo del centro, igual que si
                                cancelara desde su portal.
                            </p>
                            <div className='flex gap-2 mt-2'>
                                <button
                                    onClick={handleCancelarPorCliente}
                                    disabled={cancelandoPorCliente}
                                    className={`p-2 rounded text-white ${cancelandoPorCliente ? 'bg-gray-400' : 'bg-amber-600'}`}
                                >
                                    Cancelar su clase
                                </button>
                                <button
                                    onClick={() => setClienteACancelar(null)}
                                    className='p-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50'
                                >
                                    Volver
                                </button>
                            </div>
                        </div>
                    )}

                    {/* D2 — personas de prueba, separadas de los clientes a propósito:
                        no tienen plaza ni ficha de cliente, pero sí ocupan sitio. */}
                    {prospectosEnClase.length > 0 && (
                        <section>
                            <h3 className='font-bold text-sm uppercase tracking-wide text-purple-700 mb-2'>
                                De prueba ({prospectosEnClase.length})
                            </h3>
                            <div className='bg-white rounded-lg border border-purple-200'>
                                <PeopleTableHeader middle='Contacto' />
                                <ul className='divide-y divide-gray-100'>
                                    {prospectosEnClase.map((prospecto) => (
                                        <li key={prospecto._id} className='grid grid-cols-12 gap-2 items-center px-3 py-2'>
                                            <div className='col-span-12 lg:col-span-5 min-w-0'>
                                                <p className='text-sm font-medium text-purple-900 truncate'>{prospecto.name}</p>
                                            </div>
                                            <ContactCell email={prospecto.email} telefono={prospecto.phone} />
                                            <div className='col-span-12 lg:col-span-2 flex lg:justify-end'>
                                                <IconButton
                                                    size='sm'
                                                    tone='danger'
                                                    label='Quitar de esta clase'
                                                    onClick={() => quitarProspecto({ eventId: event._id, prospectId: prospecto._id })}
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>
                    )}

                    {/* D1 — quien espera sitio en esta clase, en orden de turno. El
                        portal del cliente nunca enseña la cola; aquí sí, para poder
                        responder cuando alguien pregunta si va a entrar. */}
                    {enEspera.length > 0 && (
                        <section>
                            <h3 className='font-bold text-sm uppercase tracking-wide text-amber-700 mb-2'>
                                En lista de espera ({enEspera.length})
                            </h3>
                            <div className='bg-white rounded-lg border border-amber-200'>
                                <PeopleTableHeader middle='Contacto' />
                                <ul className='divide-y divide-gray-100'>
                                    {enEspera.map((entrada, posicion) => (
                                        <li key={entrada._id} className='grid grid-cols-12 gap-2 items-center px-3 py-2'>
                                            <div className='col-span-12 lg:col-span-5 min-w-0 flex items-baseline gap-2'>
                                                <span className='text-xs font-bold text-amber-700 shrink-0'>{posicion + 1}.</span>
                                                <div className='min-w-0'>
                                                    <p className='text-sm font-medium text-amber-900 truncate'>
                                                        {entrada.clientId?.nombre ?? 'Cliente dado de baja'}
                                                    </p>
                                                    <p className='text-[11px] text-gray-400'>
                                                        Se apuntó el {formatDate(entrada.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                            <ContactCell email={entrada.clientId?.email} telefono={entrada.clientId?.telefono} />
                                            {/* El hueco solo en escritorio: ahí mantiene la columna
                                                alineada con las dos tablas de arriba. Apilado en
                                                móvil no hay columna que alinear y deja un vacío. */}
                                            <div className='hidden lg:flex lg:col-span-2 lg:justify-end'>
                                                <IconButtonSlot size='sm' />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <p className='text-[11px] text-gray-400 mt-1'>
                                Al quedar una plaza libre entra el primero que tenga una recuperación válida.
                            </p>
                        </section>
                    )}

                    {/* P6 — la lista de esta clase, dentro del mismo contenedor que
                        las otras tres: es una cuarta tabla de las mismas personas,
                        y colgada fuera se leía como otra pantalla pegada al borde. */}
                    <ClassRegister eventoId={event._id} />
                </div>

                {showAddProspect && (
                    <div className='p-2 border-t'>
                        <h3 className='font-bold text-lg'>Agregar persona de prueba</h3>
                        {!creandoNuevo ? (
                            <>
                                <div className='p-2'>
                                    <Select
                                        options={todosLosProspectos
                                            .filter((p) => !p.convertedClientId)
                                            .filter((p) => !prospectosEnClase.some((yaEsta) => yaEsta._id === p._id))
                                            .map((p) => ({ value: p._id, label: `${p.name} — ${p.phone}` }))}
                                        onChange={(opcion) => setSelectedProspectId(opcion?.value || '')}
                                        placeholder='Buscar por nombre o teléfono…'
                                        noOptionsMessage={() => 'No hay personas de prueba registradas'}
                                        isClearable
                                    />
                                </div>
                                <div className='flex gap-2 p-2'>
                                    <button
                                        onClick={handleAddProspect}
                                        disabled={!selectedProspectId || apuntandoProspecto}
                                        className={`p-2 rounded text-white ${!selectedProspectId || apuntandoProspecto ? 'bg-gray-400' : 'bg-purple-600'}`}
                                    >
                                        Apuntar
                                    </button>
                                    <button onClick={() => setCreandoNuevo(true)} className='p-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50'>
                                        Es nueva, registrarla
                                    </button>
                                    <button onClick={cerrarAddProspect} className='p-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50'>
                                        Cancelar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='grid grid-cols-1 md:grid-cols-3 gap-2 p-2'>
                                    <input
                                        className='p-2 border border-gray-300 rounded'
                                        placeholder='Nombre'
                                        value={nuevoProspecto.name}
                                        onChange={(e) => setNuevoProspecto((p) => ({ ...p, name: e.target.value }))}
                                    />
                                    <input
                                        className='p-2 border border-gray-300 rounded'
                                        placeholder='Teléfono'
                                        value={nuevoProspecto.phone}
                                        onChange={(e) => setNuevoProspecto((p) => ({ ...p, phone: e.target.value }))}
                                    />
                                    <input
                                        className='p-2 border border-gray-300 rounded'
                                        placeholder='Correo'
                                        type='email'
                                        value={nuevoProspecto.email}
                                        onChange={(e) => setNuevoProspecto((p) => ({ ...p, email: e.target.value }))}
                                    />
                                </div>
                                <p className='px-2 text-[11px] text-gray-400'>
                                    No hace falta el DNI: se pide solo si luego se da de alta como cliente.
                                </p>
                                <div className='flex gap-2 p-2'>
                                    <button
                                        onClick={handleCrearYApuntar}
                                        disabled={creandoProspecto || apuntandoProspecto}
                                        className={`p-2 rounded text-white ${creandoProspecto || apuntandoProspecto ? 'bg-gray-400' : 'bg-purple-600'}`}
                                    >
                                        Registrar y apuntar
                                    </button>
                                    <button onClick={() => setCreandoNuevo(false)} className='p-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50'>
                                        Volver a la lista
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {showAddConRecuperacion && (
                    <div className='p-2 border-t'>
                        <h3 className='font-bold text-lg'>Añadir con recuperación</h3>
                        <p className='px-2 text-[11px] text-gray-500'>
                            Solo salen los clientes con una recuperación que sirve para esta clase (plazo y nivel).
                            Se gasta la que antes caduca.
                        </p>
                        <div className='p-2'>
                            <Select
                                options={clientesConRecuperacion.map((c) => ({
                                    value: c._id,
                                    label: `${c.nombre}${c.telefono ? ` — ${c.telefono}` : ''} (hasta el ${formatDate(c.rescheduleDeadline)})`,
                                }))}
                                onChange={(opcion) => setClienteConRecuperacionId(opcion?.value || '')}
                                placeholder={cargandoConRecuperacion ? 'Cargando…' : 'Buscar cliente…'}
                                noOptionsMessage={() => 'Ningún cliente tiene una recuperación válida para esta clase'}
                                isLoading={cargandoConRecuperacion}
                                isClearable
                            />
                        </div>
                        <div className='flex gap-2 p-2'>
                            <button
                                onClick={handleAnadirConRecuperacion}
                                disabled={!clienteConRecuperacionId || anadiendoConRecuperacion}
                                className={`p-2 rounded text-white ${!clienteConRecuperacionId || anadiendoConRecuperacion ? 'bg-gray-400' : 'bg-emerald-600'}`}
                            >
                                Apuntar
                            </button>
                            <button onClick={cerrarAddConRecuperacion} className='p-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50'>
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}
                {showAddClient && (
                    <div className="p-2">
                        <h3 className='font-bold text-lg'>Agregar Cliente</h3>
                        {isLoadingClientes && <p>Cargando clientes...</p>}
                        {isErrorClientes && <p>Error al cargar los clientes</p>}
                        {clientesDisponibles && (
                            <div className='flex justify-between gap-4 p-1 items-center'>
                                <div className='items-center flex-1'>
                                    <Select
                                        options={clientesDisponibles
                                            .filter(cliente => !event.clientesInscritos.some((inscrito: Client) => inscrito._id === cliente._id))
                                            .map(cliente => ({ value: cliente._id, label: cliente.nombre }))}
                                        placeholder="Seleccione un Cliente"
                                        onChange={
                                            (selectedOption) => setSelectedClientId(selectedOption?.value || '')
                                        }
                                        value={
                                            selectedClientId !== '' ? 
                                                {value: selectedClientId, label: clientesDisponibles.find(cliente => cliente._id === selectedClientId)?.nombre }
                                            : 
                                                null
                                            } 
                                        isSearchable
                                    />
                                </div>
                                
                                <div className='flex justify-center gap-2'>
                                    <button
                                        onClick={handleAddClient}
                                        className="mt-2 bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
                                    >
                                        Agregar
                                    </button>
                                    <button
                                        onClick={handleCloseShowAddClient}
                                        className="mt-2 bg-red-500 text-white p-2 rounded hover:bg-red-600"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {showConfirmationModal && (
                    <ConfirmationModal
                        isOpen={showConfirmationModal}
                        onClose={() => setShowConfirmationModal(false)}
                        onConfirm={handleOnConfirm}
                        confirmMessage={confirmationMessage}
                    />
                )}
                {showCancelClassModal && (
                    <CancelClassModal
                        enrolledCount={event.clientesInscritos.length}
                        isLoading={isCancellingClass}
                        onConfirm={handleConfirmCancelClass}
                        onClose={() => setShowCancelClassModal(false)}
                    />
                )}
                {showReplicarEvento && (
                    <ReplicarEvento
                        event={event}
                        onClose={() => setShowReplicarEvento(false)}
                        startDate={startDate}
                        endDate={endDate}
                    />
                )}
            </div>
        </div>
    );
};

export default EventDetail;